import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OtherPanel } from './WalletShared';
import { ModalSheet } from '../ui/ModalSheet';
import { ThemedField, ThemedInput } from '../ui/ThemedSurface';
import { Button } from '../ui/Button';
import { ApiError } from '../../api/client';
import { usersAPI } from '../../api/users';
import { COLORS, WALLET_COLORS as C } from '@/constants';

type Translate = (key: string) => string;

export function formatHangDollars(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '0';
  const hasFrac = Math.abs(n % 1) > 1e-9;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: hasFrac ? 2 : 0,
    maximumFractionDigits: hasFrac ? 2 : 0,
  });
}

export function HangTradePanel({
  t,
  hangBuyIng,
  hangSellIng,
}: {
  t: Translate;
  hangBuyIng?: string;
  hangSellIng?: string;
}) {
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [txPassword, setTxPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const buyLabel = formatHangDollars(hangBuyIng ?? '0');
  const sellLabel = formatHangDollars(hangSellIng ?? '0');
  const displayBuy = revealed ? `${buyLabel} $` : '**';
  const displaySell = revealed ? `${sellLabel} $` : '**';

  function requestReveal() {
    setError(null);
    setTxPassword('');
    setPwdOpen(true);
  }

  async function submitReveal() {
    const pwd = txPassword.trim();
    if (!pwd) {
      setError(tr('common.enterTxPassword', '请输入交易密码'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await usersAPI.verifyTxPassword({ transaction_password: pwd });
      setRevealed(true);
      setPwdOpen(false);
      setTxPassword('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : tr('common.error', '操作失败'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <OtherPanel className="px-1.5">
        <div className="flex items-center gap-2 p-2.5">
          <img src="/brand/assets/exchange.png" alt="" className="size-[25px] object-contain" draggable={false} />
          <span className="text-sm font-semibold text-white">{t('wallet.hangTradeTitle')}</span>
          <img src="/brand/icon-uo-assets.png" alt="" className="ml-1 size-4 object-contain opacity-80" draggable={false} />
        </div>
        <div className="grid grid-cols-2 gap-0.5 pb-3">
          <button
            type="button"
            onClick={() => {
              if (!revealed) {
                requestReveal();
                return;
              }
              navigate('/buy-queue');
            }}
            className="flex min-h-[60px] flex-col items-center justify-center gap-2 border px-1 py-3 transition active:scale-[0.98]"
            style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}
          >
            <div className="truncate text-base font-semibold leading-none" style={{ color: COLORS.iconGlobal }}>
              {displayBuy}
            </div>
            <div className="truncate text-[9px] leading-none" style={{ color: COLORS.iconColor }}>
              {t('wallet.hangBuy')}
            </div>
          </button>
          <button
            type="button"
            onClick={requestReveal}
            className="flex min-h-[60px] flex-col items-center justify-center gap-2 border px-1 py-3 transition active:scale-[0.98]"
            style={{ borderColor: C.otherAsset, background: C.bgAsset, boxShadow: C.shadowAsset }}
          >
            <div className="truncate text-base font-semibold leading-none" style={{ color: COLORS.iconGlobal }}>
              {displaySell}
            </div>
            <div className="truncate text-[9px] leading-none" style={{ color: COLORS.iconColor }}>
              {t('wallet.hangSell')}
            </div>
          </button>
        </div>
      </OtherPanel>

      <ModalSheet
        open={pwdOpen}
        onClose={() => {
          if (busy) return;
          setPwdOpen(false);
          setTxPassword('');
          setError(null);
        }}
        title={tr('wallet.revealHangTitle', '查看掛買/掛賣')}
        footer={
          <Button type="button" fullWidth isLoading={busy} disabled={!txPassword.trim()} onClick={() => void submitReveal()}>
            {tr('common.confirm', '确认')}
          </Button>
        }
      >
        <div className="flex flex-col gap-3 py-1">
          <p className="text-xs leading-relaxed" style={{ color: COLORS.iconColor }}>
            {tr('wallet.revealHangHint', '请输入交易密码后查看金额')}
          </p>
          <ThemedField label={tr('common.txPassword', '交易密码')} required>
            <ThemedInput
              type="password"
              autoComplete="current-password"
              value={txPassword}
              onChange={(e) => setTxPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitReveal();
              }}
              placeholder={tr('common.enterTxPassword', '请输入交易密码')}
            />
          </ThemedField>
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      </ModalSheet>
    </>
  );
}
